import axios, { AxiosInstance, AxiosRequestConfig, AxiosResponse, AxiosError } from 'axios';
import type { HttpClient } from '../../domain/interfaces/HttpClient';
import type { HttpClientConfig, HttpClientRequestConfig } from '../../domain/models/httpClient.models';
import { handleApiError } from '../../error/errorHandler';
import { getApiBaseUrl } from '@/core/utils/apiUtils';

/**
 * Infrastructure Adapter: AxiosHttpClientAdapter
 * Implements the HttpClient domain interface using Axios
 * This is the infrastructure layer - contains implementation details
 */

/**
 * Axios HTTP Client Adapter
 * Implements the HttpClient domain interface using Axios
 */
export class AxiosHttpClientAdapter implements HttpClient {
  private client: AxiosInstance;

  constructor(config?: HttpClientConfig) {
    this.client = axios.create({
      baseURL: config?.baseURL ?? getApiBaseUrl(),
      headers: {
        'Content-Type': 'application/json',
        ...config?.headers,
      },
      timeout: config?.timeout ?? 30000, // 30 seconds default, this is when api is not responding, prevents infinite loading and memory leaks
      withCredentials: config?.withCredentials ?? false, // Don't send credentials to avoid CORS issues, this is when we need to send browser cookies or headers to the server
    });

    this.setupInterceptors();
  }

  /**
   * Setup request and response interceptors
   */
  private setupInterceptors(): void {
    // Request interceptor - can be used to add auth tokens, modify requests, etc.
    this.client.interceptors.request.use(
      (config) => {
        // Log request URL in development for debugging
        if (import.meta.env.DEV) {
          const fullUrl = `${config.baseURL || ''}${config.url || ''}`;
          console.log('API Request:', config.method?.toUpperCase(), fullUrl);
        }
        
        // Add auth token if available
        // const token = localStorage.getItem('authToken');
        // if (token) {
        //   config.headers.Authorization = `Bearer ${token}`;
        // }
        return config;
      },
      (error: AxiosError) => {
        return Promise.reject(error);
      }
    );

    // Response interceptor - handles errors globally
    this.client.interceptors.response.use(
      (response: AxiosResponse) => {
        return response;
      },
      (error: AxiosError) => {
        // Convert Axios error to domain error using error handler
        const apiError = handleApiError(error);
        
        // Log error in development
        if (import.meta.env.DEV) {
          console.error('API Error:', apiError.name, apiError.message, {
            statusCode: apiError.statusCode,
            code: apiError.code,
          });
        }
        
        return Promise.reject(apiError);
      }
    );
  }

  /**
   * Convert domain HttpClientRequestConfig to Axios AxiosRequestConfig
   */
  private toAxiosConfig(config?: HttpClientRequestConfig): AxiosRequestConfig {
    if (!config) return {};
    
    return {
      headers: config.headers,
      params: config.params,
      timeout: config.timeout,
    };
  }

  /**
   * GET request
   */
  async get<T = unknown>(url: string, config?: HttpClientRequestConfig): Promise<T> {
    const response = await this.client.get<T>(url, this.toAxiosConfig(config));
    return response.data;
  }

  /**
   * POST request
   */
  async post<T = unknown, D = unknown>(
    url: string,
    data?: D,
    config?: HttpClientRequestConfig
  ): Promise<T> {
    const response = await this.client.post<T>(url, data, this.toAxiosConfig(config));
    return response.data;
  }

  /**
   * PUT request
   */
  async put<T = unknown, D = unknown>(
    url: string,
    data?: D,
    config?: HttpClientRequestConfig
  ): Promise<T> {
    const response = await this.client.put<T>(url, data, this.toAxiosConfig(config));
    return response.data;
  }

  /**
   * PATCH request
   */
  async patch<T = unknown, D = unknown>(
    url: string,
    data?: D,
    config?: HttpClientRequestConfig
  ): Promise<T> {
    const response = await this.client.patch<T>(url, data, this.toAxiosConfig(config));
    return response.data;
  }

  /**
   * DELETE request
   */
  async delete<T = unknown>(url: string, config?: HttpClientRequestConfig): Promise<T> {
    const response = await this.client.delete<T>(url, this.toAxiosConfig(config));
    return response.data;
  }
}

